import { useState, useEffect, useCallback, useContext, useRef } from 'react';
import { AuthContext } from '../contexts/AuthContext';
import { filesAPI } from '../services/api';

export default function useFileList() {
  const { user, refreshUser } = useContext(AuthContext);
  const [transcripts, setTranscripts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const intervalRef = useRef(null);

  const loadFiles = useCallback(async () => {
    try {
      const response = await filesAPI.list();
      const files = response.data.transcripts;
      setTranscripts(files);
      setError(null);

      // Check if we need to keep polling
      const hasPendingFiles = files.some(
        (t) => t.status === 'pending' || t.status === 'processing'
      );

      // Start polling if there are pending files and no interval running
      if (hasPendingFiles && !intervalRef.current) {
        intervalRef.current = setInterval(() => {
          loadFiles();
          refreshUser();
        }, 5000);
      }

      // Stop polling if no more pending files
      if (!hasPendingFiles && intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
        refreshUser();
      }
    } catch (err) {
      setError('Failed to load files');
    } finally {
      setLoading(false);
    }
  }, [refreshUser]);

  useEffect(() => {
    loadFiles();

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [loadFiles]);

  const handleDelete = async (filename) => {
    if (!window.confirm('Are you sure you want to delete this file?')) {
      return;
    }

    try {
      await filesAPI.delete(filename);
      setTranscripts((prev) => prev.filter((t) => t.filename !== filename));
      refreshUser();
    } catch (err) {
      setError('Failed to delete file');
    }
  };

  return {
    user,
    transcripts,
    loading,
    error,
    loadFiles,
    handleDelete,
  };
}
